import { ChangeDetectionStrategy, Component, Input, EventEmitter, Output, SimpleChanges } from '@angular/core';
import { FoodMeasure, LABEL_SERVING_MEASURE_NAME } from '@cf-shared/data-model/food';
import { UserDb } from 'services/user-db.service';
import { TranslatedString } from 'models/translated-string';
import { translateMeasureName, PICKABLE_MEASURE_NAMES, buildMeasurePickerNames } from 'util/formatting';
import { RECIPE_SERVINGS_MEASURE_NAME } from '@cf-shared/data-model/recipe';
import { decideServingsMeasureSync, ServingsMeasureSyncAction } from 'pages/edit-food/recipe-measures';

import { LL } from 'i18n/i18n.service';
@Component({
    selector: 'measures-list',
    templateUrl: 'measures-list.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class MeasuresListComponent {
  protected LL = LL;
  // the name a brand-new measure row starts out with. Taken from util/formatting.ts's
  // PICKABLE_MEASURE_NAMES (not hand-copied), so a row can never be seeded with a name the
  // picker does not offer -- such a row shows its name raw and untranslated, because that is
  // what cs-select falls back to when no option matches.
  private readonly DEFAULT_MEASURE_NAME: string = PICKABLE_MEASURE_NAMES[0];

  @Input() totalWeight: number;

  @Input() measures: FoodMeasure[] = [];
  @Input() isReadOnly: boolean;
  // when true this list holds a recipe's single measure, whose name is always
  // RECIPE_SERVINGS_MEASURE_NAME and whose `amount` is how many servings the recipe makes.
  // Two things follow, and both are needed (#4380): there is no "Add Measure" button, because
  // a recipe has only this one measure; and the name is shown as fixed text rather than a
  // picker, because changing it used to delete the measure and take the servings count with it.
  @Input() lockToPreparedMeasure: boolean = false;

  @Output() updateMeasures = new EventEmitter<FoodMeasure[]>();

  protected showSpinner: boolean;
  protected searchItems: string[];

  constructor(private userDb: UserDb) { }

  // translates a known measure name (one of the searchItems the picker offers) for display;
  // delegates to the shared helper in util/formatting.ts so the picker and every other
  // measure-name display (formatServingSize, edit-food.ts, edit-ingredient.ts) translate the
  // same names the same way
  protected measureLabel(name: string): TranslatedString {
    return translateMeasureName(name);
  }

  // the fixed name shown instead of the picker when lockToPreparedMeasure is set. It reads
  // "serving" -- the raw canonical value stays RECIPE_SERVINGS_MEASURE_NAME, only the text
  // the user sees is translated.
  protected get preparedMeasureLabel(): TranslatedString {
    return translateMeasureName(RECIPE_SERVINGS_MEASURE_NAME);
  }

  shouldShowMeasure(measure: FoodMeasure) {
    // the US nutrition label's serving size is edited by its own field further up the food
    // editor, so showing it here as well would be the same number in two places
    return measure.name !== LABEL_SERVING_MEASURE_NAME;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes.totalWeight) return;

    // everything below is about the ONE measure a recipe carries, so decideServingsMeasureSync
    // answers "nothing to do" for a list that does not belong to a recipe. That answer is the
    // point of the call: this used to run for every food, and a plain food's list would invent a
    // measure named RECIPE_SERVINGS_MEASURE_NAME which the food editor removed again straight
    // away (deferred finding df-8dd9d4e6ec1d). Its comment says the rest.
    const action = decideServingsMeasureSync(this.lockToPreparedMeasure, this.measures);
    if (action === ServingsMeasureSyncAction.None) return;

    if (action === ServingsMeasureSyncAction.Seed) {
      this.addMeasure(changes.totalWeight.currentValue);
      return;
    }

    // the recipe already has its servings measure, so keep its weight in step with the total
    this.measures = this.measures.map(measure => {
      if (measure.name === RECIPE_SERVINGS_MEASURE_NAME) {
        measure.weight_g = changes.totalWeight.currentValue;
      }

      return measure;
    });
  }

  ngOnInit() {
    void this.ngOnInitAsync();
  }

  async ngOnInitAsync() {
    await this.mergeAvailableMeasuresAsync();
  }

  async mergeAvailableMeasuresAsync() {
    const customMeasures = await this.userDb.loadCustomMeasuresAsync();
    this.searchItems = buildMeasurePickerNames(customMeasures.names, this.measures.map(m => m.name));
  }

  addMeasure(totalWeight?: number) {
    const measure = {
      name: this.DEFAULT_MEASURE_NAME,
      amount: 1,
      weight_g: 0,
    };

    if (totalWeight !== undefined) {
      measure.amount = 1;
      measure.name = RECIPE_SERVINGS_MEASURE_NAME;
      measure.weight_g = totalWeight;
    }

    this.measures.push(measure);
    this.updateMeasures.emit(this.measures);
  }

  updateMeasure(measure: FoodMeasure) {
    this.measures = this.measures.map(currentMeasure => {
      if (currentMeasure.name === measure.name) {
        currentMeasure = measure;
      }

      return currentMeasure;
    });

    this.updateMeasures.emit(this.measures);
  }

  hasMeasureWeightWarning(measure: FoodMeasure): boolean {
    return !measure.weight_g && measure.name !== RECIPE_SERVINGS_MEASURE_NAME;
  }

  removeMeasure(measure: any) {
    this.measures = this.measures.filter(m => m !== measure);
    this.updateMeasures.emit(this.measures);
  }
}
